import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import MentionEditor from './NotesSectionMentionEditor';

/**
 * NotesSection Component
 * 
 * Enterprise-style notes and comments management
 * Supports recruiter notes and internal comments for collaboration
 */
const NotesSection = ({ 
  recruiterNotes = [], 
  internalComments = [],
  onAddNote,
  onAddComment,
  readOnly = false 
}) => {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [newNote, setNewNote] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newCommentMentions, setNewCommentMentions] = useState([]); // Track mentions from editor
  const [activeTab, setActiveTab] = useState('notes'); // 'notes' or 'comments'

  const handleAddNote = () => {
    if (!newNote.trim() || !onAddNote) return;
    onAddNote(newNote.trim());
    setNewNote('');
  };

  const handleAddComment = () => {
    if (!newComment.trim() || !onAddComment) return;
    
    // CRITICAL: Extract user identifiers from selected mentions
    // Also try to extract from the editor if mentions weren't captured in state
    let finalMentions = [...newCommentMentions];
    
    // If no mentions in state, try to extract from editor DOM as fallback
    if (finalMentions.length === 0) {
      console.warn('[NotesSection] No mentions in state, attempting to extract from editor DOM');
      // The editor component should have already extracted and sent mentions,
      // but as a safety fallback, we can check here if needed
    }
    
    const mentionIdentifiers = finalMentions
      .map(m => {
        // Email is the most reliable identifier (unique across system)
        if (m && m.email) {
          return m.email;
        }
        // Fallback to database ID
        if (m && (m._id || m.id)) {
          return m._id || m.id;
        }
        return null;
      })
      .filter(id => id); // Remove any undefined/null values
    
    console.log('[NotesSection] Adding comment with mentions:', {
      commentLength: newComment.trim().length,
      selectedMentionsCount: finalMentions.length,
      selectedMentions: finalMentions.map(m => ({ email: m?.email, name: m?.name, id: m?._id || m?.id })),
      mentionIdentifiers: mentionIdentifiers,
      willSendToBackend: mentionIdentifiers.length > 0 ? mentionIdentifiers : undefined
    });
    
    // Send explicit mentions to backend - these are the EXACT users selected
    onAddComment(newComment.trim(), mentionIdentifiers.length > 0 ? mentionIdentifiers : undefined);
    
    // Clear editor state
    setNewComment('');
    setNewCommentMentions([]);
  };

  const handleCommentTextChange = (text) => {
    setNewComment(text);
  };

  const handleCommentMentionsChange = (mentions) => {
    console.log('[NotesSection] Mentions changed from editor:', {
      count: mentions?.length || 0,
      mentions: mentions?.map(m => ({ email: m.email, name: m.name, id: m._id || m.id })) || []
    });
    // CRITICAL: Store the exact user objects selected in the editor
    // These will be used to extract identifiers when submitting
    setNewCommentMentions(mentions || []);
  };


  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ padding: '1rem 0' }}>
      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '2px solid #E5E7EB',
        marginBottom: '1rem'
      }}>
        <button
          onClick={() => setActiveTab('notes')}
          style={{
            padding: '0.75rem 1rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'notes' ? '2px solid #1E40AF' : '2px solid transparent',
            color: activeTab === 'notes' ? '#1E40AF' : '#6B7280',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: activeTab === 'notes' ? '600' : '500',
            marginBottom: '-2px'
          }}
        >
          Notes ({recruiterNotes?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('comments')}
          style={{
            padding: '0.75rem 1rem',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'comments' ? '2px solid #1E40AF' : '2px solid transparent',
            color: activeTab === 'comments' ? '#1E40AF' : '#6B7280',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: activeTab === 'comments' ? '600' : '500',
            marginBottom: '-2px'
          }}
        >
          Internal Comments ({internalComments?.length || 0})
        </button>
      </div>

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div>
          {/* Notes List */}
          <div style={{ marginBottom: '1rem', maxHeight: '300px', overflowY: 'auto' }}>
            {recruiterNotes && recruiterNotes.length > 0 ? (
              recruiterNotes
                .slice()
                .reverse()
                .map((note, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '0.75rem',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      marginBottom: '0.75rem'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.5rem'
                    }}>
                      <div>
                        <span style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          {note.addedBy?.fullName || note.addedBy?.email || 'Unknown'}
                        </span>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#9CA3AF',
                          marginLeft: '0.5rem'
                        }}>
                          {formatDate(note.addedAt)}
                        </span>
                      </div>
                    </div>
                    <p style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {note.note}
                    </p>
                  </div>
                ))
            ) : (
              <p style={{ 
                fontSize: '0.875rem', 
                color: '#9CA3AF', 
                fontStyle: 'italic',
                textAlign: 'center',
                padding: '2rem'
              }}>
                No notes yet
              </p>
            )}
          </div>

          {/* Add Note Form */}
          {!readOnly && (
            <div>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note about this candidate..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  marginBottom: '0.5rem'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  background: newNote.trim() ? '#1E40AF' : '#9CA3AF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: newNote.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Add Note
              </button>
            </div>
          )}
        </div>
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && (
        <div>
          {/* Comments List */}
          <div style={{ marginBottom: '1rem', maxHeight: '300px', overflowY: 'auto' }}>
            {internalComments && internalComments.length > 0 ? (
              internalComments
                .slice()
                .reverse()
                .map((comment, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '0.75rem',
                      background: comment.pinned ? '#FEF3C7' : '#F9FAFB',
                      border: `1px solid ${comment.pinned ? '#FDE68A' : '#E5E7EB'}`,
                      borderRadius: '6px',
                      marginBottom: '0.75rem'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.5rem'
                    }}>
                      <div>
                        <span style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          {comment.addedBy?.fullName || comment.addedBy?.email || 'Unknown'}
                        </span>
                        {comment.pinned && (
                          <span style={{ 
                            fontSize: '0.75rem', 
                            color: '#92400E',
                            marginLeft: '0.5rem',
                            fontWeight: '500'
                          }}>
                            📌 Pinned
                          </span>
                        )}
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#9CA3AF',
                          marginLeft: '0.5rem'
                        }}>
                          {formatDate(comment.addedAt)}
                        </span>
                      </div>
                    </div>
                    <p style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: '1.6'
                    }}>
                      {(() => {
                        // Build a set of exact mention patterns from saved mentions data
                        const mentionPatterns = new Set();
                        if (comment.mentions && comment.mentions.length > 0) {
                          comment.mentions.forEach(m => {
                            const fullName = m.fullName || m.name || m.displayName || '';
                            if (fullName) {
                              mentionPatterns.add(`@${fullName}`);
                            }
                            // Also add first name only (in case user typed @FirstName)
                            const firstName = fullName.split(' ')[0];
                            if (firstName) {
                              mentionPatterns.add(`@${firstName}`);
                            }
                          });
                        }
                        
                        // If we have mention patterns, use them for exact matching
                        if (mentionPatterns.size > 0) {
                          // Sort patterns by length (longest first) to match full names before partial
                          const sortedPatterns = Array.from(mentionPatterns).sort((a, b) => b.length - a.length);
                          const patternRegex = new RegExp(`(${sortedPatterns.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})(?=\\s|$|[.,!?;:])`, 'g');
                          
                          return comment.comment.split(patternRegex).map((part, idx) => {
                            if (mentionPatterns.has(part.trim())) {
                              return (
                                <span 
                                  key={idx} 
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    background: '#DBEAFE',
                                    color: '#1E40AF',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    fontSize: '0.8125rem',
                                    margin: '0 0.125rem',
                                    border: '1px solid #BFDBFE',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {part.trim()}
                                </span>
                              );
                            }
                            return <span key={idx}>{part}</span>;
                          });
                        }
                        
                        // Fallback: Use regex matching if no mention data available
                        return comment.comment.split(/(@\w+(?:\s+\w+){0,2})\b(?=\s|$|[.,!?;:])/g).map((part, idx) => {
                          if (part.trim().startsWith('@')) {
                            const mentionText = part.trim();
                            if (mentionText.length <= 30 && 
                                mentionText.split(/\s+/).length <= 3 && 
                                !/[.,!?;:]/.test(mentionText)) {
                              return (
                                <span 
                                  key={idx} 
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    background: '#DBEAFE',
                                    color: '#1E40AF',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    fontSize: '0.8125rem',
                                    margin: '0 0.125rem',
                                    border: '1px solid #BFDBFE',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {mentionText}
                                </span>
                              );
                            }
                          }
                          return <span key={idx}>{part}</span>;
                        });
                      })()}
                    </p>
                    {comment.mentions && comment.mentions.length > 0 && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6B7280' }}>
                        Mentioned: {
                          // Deduplicate mentions by email before displaying
                          Array.from(new Map(comment.mentions.map(m => [m.email || m._id || m.id, m])).values())
                            .map(m => m.fullName || m.name || m.displayName || m.email || 'Unknown')
                            .join(', ')
                        }
                      </div>
                    )}
                  </div>
                ))
            ) : (
              <p style={{ 
                fontSize: '0.875rem', 
                color: '#9CA3AF', 
                fontStyle: 'italic',
                textAlign: 'center',
                padding: '2rem'
              }}>
                No internal comments yet
              </p>
            )}
          </div>

          {/* Add Comment Form */}
          {!readOnly && (
            <div>
              <MentionEditor
                value={newComment}
                onChange={handleCommentTextChange}
                onMentionsChange={handleCommentMentionsChange}
                placeholder="Add an internal comment (visible to all recruiters). Use @name to mention someone..."
              />
              <button
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  background: newComment.trim() ? '#1E40AF' : '#9CA3AF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: newComment.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Add Comment
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotesSection;
